import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["content", "name"]
  static classes = ["expanded", "collapsed"]

  connect() {
    // Check if this task should be opened by default based on URL parameter
    this.checkForAutoOpen()
    
    // Check if this task should be opened based on stored state
    this.checkForStoredOpenState()
    
    // Check if this is a newly created subtask that should open its parent
    this.checkForNewSubtask()
  }

  checkForAutoOpen() {
    const urlParams = new URLSearchParams(window.location.search)
    const taskIdParam = urlParams.get('task_id')
    const idParam = urlParams.get('id')
    const currentTaskId = this.element.dataset.taskId
    
    // Check if this task should be opened based on task_id or id parameter
    if ((taskIdParam && currentTaskId && taskIdParam === currentTaskId) ||
        (idParam && currentTaskId && idParam === currentTaskId)) {
      // Auto-open this task
      this.openContent()
    }
  }

  checkForStoredOpenState() {
    const currentTaskId = this.element.dataset.taskId
    const openTasks = this.getOpenTasks()
    
    if (openTasks.includes(currentTaskId)) {
      this.openContent()
    }
  }

  checkForNewSubtask() {
    const currentTaskId = this.element.dataset.taskId
    const urlParams = new URLSearchParams(window.location.search)
    const newSubtaskParentId = urlParams.get('new_subtask_parent_id')
    
    if (newSubtaskParentId && currentTaskId === newSubtaskParentId) {
      // This is the parent of a newly created subtask, open it
      this.openContent()
      
      // Remove the parameter from URL to avoid reopening on future page loads
      this.removeUrlParameter('new_subtask_parent_id')
    }
  }

  openContent() {
    if (this.hasContentTarget) {
      const content = this.contentTarget
      const nameElement = this.nameTarget
      
      content.style.display = 'block'
      if (this.hasExpandedClass) {
        nameElement.classList.add(this.expandedClass)
      }
      if (this.hasCollapsedClass) {
        nameElement.classList.remove(this.collapsedClass)
      }
    }
  }

  stopPropagation(event) {
    // Stop event from bubbling up to toggleContent
    event.stopPropagation()
  }

  toggleContent(event) {
    // Don't toggle if clicking on interactive elements
    if (this.isInteractiveElement(event.target)) {
      return
    }
    
    event.preventDefault()
    
    if (this.hasContentTarget) {
      const content = this.contentTarget
      const nameElement = this.nameTarget
      
      if (content.style.display === 'none' || !content.style.display) {
        // Show content
        content.style.display = 'block'
        if (this.hasExpandedClass) {
          nameElement.classList.add(this.expandedClass)
        }
        if (this.hasCollapsedClass) {
          nameElement.classList.remove(this.collapsedClass)
        }
        // Store that this task is now open
        this.addOpenTask()
      } else {
        // Hide content
        content.style.display = 'none'
        if (this.hasExpandedClass) {
          nameElement.classList.remove(this.expandedClass)
        }
        if (this.hasCollapsedClass) {
          nameElement.classList.add(this.collapsedClass)
        }
        // Store that this task is now closed
        this.removeOpenTask()
      }
    }
  }

  // Store open task state
  addOpenTask() {
    const currentTaskId = this.element.dataset.taskId
    const openTasks = this.getOpenTasks()
    
    if (!openTasks.includes(currentTaskId)) {
      openTasks.push(currentTaskId)
      this.setOpenTasks(openTasks)
    }
  }

  removeOpenTask() {
    const currentTaskId = this.element.dataset.taskId
    const openTasks = this.getOpenTasks()
    
    const filteredTasks = openTasks.filter(id => id !== currentTaskId)
    this.setOpenTasks(filteredTasks)
  }

  getOpenTasks() {
    const stored = localStorage.getItem('procedure_open_tasks')
    return stored ? JSON.parse(stored) : []
  }

  setOpenTasks(tasks) {
    localStorage.setItem('procedure_open_tasks', JSON.stringify(tasks))
  }

  // Remove URL parameter without page reload
  removeUrlParameter(paramName) {
    const url = new URL(window.location)
    url.searchParams.delete(paramName)
    window.history.replaceState({}, '', url)
  }

  // Clear stored open tasks when they're no longer needed
  clearStoredOpenTasks() {
    localStorage.removeItem('procedure_open_tasks')
  }

  isInteractiveElement(element) {
    // Check if the clicked element or its parent is an interactive element
    const interactiveSelectors = [
      'button',
      'a',
      'input',
      'select',
      'textarea',
      '.button',
      '[data-turbo-frame]',
      '[role="button"]'
    ]
    
    return interactiveSelectors.some(selector => 
      element.matches && element.matches(selector) || 
      element.closest && element.closest(selector)
    )
  }
} 